import { FC, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAppDispatch } from '@/core/store/hooks'
import { cn } from '@bem-react/classname'
import { yupResolver } from '@hookform/resolvers/yup'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import { Back } from '@/components/UI/Back'
import { Button } from '@/components/UI/Button'
import { Card } from '@/components/UI/Card'
import Icon from '@/components/UI/Icon'
import { Logo } from '@/components/UI/Logo'

import { CodeStep } from './components/CodeStep'
import { LoginStep } from './components/LoginStep'
import {
  LoginCodeValidationSchema,
  LoginEmailValidationSchema,
  LoginModel,
  LoginPhoneValidationSchema,
  LoginView,
} from './models/Login.model'
import { loginThunk } from './store/AuthSlice'
import './styles/Login.scss'

const cnLogin = cn('Login')

export const Login: FC = (): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { formErrors, handleServerError } =
    useHandleServerError<keyof LoginModel>()

  const [actions, setActions] = useState<'email' | 'phone' | '2fa' | 'code'>(
    'email',
  )
  const [token, setToken] = useState<string>('')
  const [value, setValue] = useState<string>('')
  const [form, setForm] = useState<LoginModel | null>(null)

  const onCompleted = () => {
    methods.handleSubmit(onLogin)()
  }

  const view: LoginView[] = [
    {
      actions: 'email',
      component: <LoginStep actions={actions} onSetActions={setActions} />,
      schema: LoginEmailValidationSchema,
      defaultValues: { email: '' },
    },
    {
      actions: 'phone',
      component: <LoginStep actions={actions} onSetActions={setActions} />,
      schema: LoginPhoneValidationSchema,
      defaultValues: { phone: '' },
    },
    {
      actions: 'code',
      component: (
        <CodeStep
          type={actions}
          value={value}
          title={
            form?.type === 'email'
              ? 'register-three-title'
              : 'register-five-title'
          }
          text={
            form?.type === 'email'
              ? 'register-second-text'
              : 'register-five-text'
          }
          onCompleted={onCompleted}
        />
      ),
      schema: LoginCodeValidationSchema,
      defaultValues: { code: '', token: '' },
    },
    {
      actions: '2fa',
      component: (
        <CodeStep
          type='2fa'
          value={value}
          title={'2fa-title'}
          text={'2fa-text'}
          onCompleted={onCompleted}
        />
      ),
      schema: LoginCodeValidationSchema,
      defaultValues: { code: '', token: '' },
    },
  ]

  const viewMap: Record<'email' | 'phone' | '2fa' | 'code', LoginView> = {
    email: view[0],
    phone: view[1],
    code: view[2],
    '2fa': view[3],
  }

  const methods = useForm<LoginModel>({
    mode: 'onChange',
    defaultValues: view[0].defaultValues,
    resolver: yupResolver(view[0].schema),
  })

  useEffect(() => {
    if (token) {
      methods.setValue('token', token)
    }
  }, [token])

  useEffect(() => {
    if (formErrors) {
      Object.entries(formErrors).forEach(([key, message]) => {
        methods.setError(key as keyof LoginModel, {
          type: 'manual',
          message,
        })
      })
    }
  }, [formErrors])

  useEffect(() => {
    if (actions === 'email') methods.setValue('type', 'email')
    if (actions === 'phone') methods.setValue('type', 'phone')
  }, [actions])

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) navigate('/c')
  }, [])

  const onLogin = async (data: LoginModel) => {
    const type = data.type

    let form: LoginModel = {
      ...data,
      type,
    }

    if (type) {
      form = {
        ...form,
        [type]: data[type],
      }
    }

    if (data.step) {
      form = {
        ...form,
        code: data.code,
        token: data.token,
        type: data.type,
      }
    }

    if (type && type === 'phone') {
      delete form.email

      form.phone = `+${form.phone}`
    } else {
      delete form.phone
    }

    setForm(form)

    try {
      const res = await dispatch(loginThunk(form)).unwrap()

      if (res.next_step === 'finish') {
        await onLogin({ token: data.token, step: 'finish' })
      }

      if (!res.step && res.token && data.step === 'finish') {
        localStorage.setItem('token', res.token)
        navigate('/c')
      }

      methods.setValue('step', res.step)

      if (type) {
        methods.setValue('type', type)
      }

      const value = type ? data[type] : null
      if (value) {
        setValue(value)
      }

      if (res.step === 'verify') {
        setActions('code')
        setToken(res.token)
      }
      if (res.step === 'verify_2fa') {
        setActions('2fa')
        setToken(res.token)
      }
    } catch (error) {
      handleServerError(error)
    }
  }

  const onClick = () => {
    if (actions === 'code' && form?.type === 'email') {
      setActions('email')
      methods.reset({ ...viewMap['email'].defaultValues, email: form.email })
    }

    if (actions === 'code' && form?.type === 'phone') {
      setActions('phone')
      methods.reset({ ...viewMap['phone'].defaultValues, phone: form.phone })
    }

    if (actions === '2fa' && form?.type === 'email') {
      setActions('email')
      methods.reset({ ...viewMap['email'].defaultValues, email: form.email })
    }

    if (actions === '2fa' && form?.type === 'phone') {
      setActions('phone')
      methods.reset({ ...viewMap['phone'].defaultValues, phone: form.phone })
    }
  }

  return (
    <FormProvider {...methods}>
      <form className={cnLogin()} onSubmit={methods.handleSubmit(onLogin)}>
        <Card className={cnLogin('card')}>
          <div className={cnLogin('top')}>
            {actions !== 'email' && <Back onClick={onClick} />}
            <Logo to='/' />
          </div>

          {viewMap[actions].component}

          {(actions === 'email' || actions === 'phone') && (
            <div className={cnLogin('actions')}>
              <Button
                iconPosition='right'
                type='submit'
                isLoading={methods.formState.isSubmitting}
              >
                {t('login-btn-text')}
                <Icon name='chevron-right' stroke='white' />
              </Button>
              <Button color='blue' onClick={() => navigate('/register')}>
                {t('login-register-btn-text')}
              </Button>
            </div>
          )}
        </Card>
      </form>
    </FormProvider>
  )
}
